import React, { useState, forwardRef, useImperativeHandle, useCallback, useRef, useEffect } from 'react'
import { Table, Input, Button, Space, Modal, FormInstance, Form, notification } from 'antd';
import { SearchOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import type { TableColumnsType, TableColumnType } from 'antd';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import ListDataWorkOrder from './list_data_work_order';
import axios from 'axios';

export const statusMapping: Record<string | number, string> = {
    1: "Draf",
    2: "Approved",
    3: "Waiting for Approved",
    4: "Complete",
    5: "Canceled",
    6: "Closed",
    99: "Cancel Return",
};

interface SparepartDataType {
    key: React.Key;
    type: string;
    jobType: string;
    itemNo: string;
    description: string;
    qty: number;
    qtyReturn: number;
    uom: string;
    price: number;
    discPercent: number;
    discAmount: number;
    total: number;
}


interface OperationDataType {
    key: React.Key;
    type: string;
    jobType: string;
    operationNo: string;
    description: string;
    frt: number;
    price: number;
    discPercent: number;
    discAmount: number;
    total: number;
}

type SparepartDataIndex = keyof SparepartDataType;
type OperationDataIndex = keyof OperationDataType;

const ResizableTitle = (props: React.HTMLAttributes<any> & { onColumnResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number }) => {
    const { onColumnResize, width, ...restProps } = props;
    if (!width) return <th {...restProps} />;
    return (
        <Resizable width={width} height={0} handle={<span className="react-resizable-handle" onClick={(e) => e.stopPropagation()} />} onResize={onColumnResize} draggableOpts={{ enableUserSelectHack: false }}>
            <th {...restProps} />
        </Resizable>
    );
};


const InvoiceReturnTable = forwardRef((props: { form: FormInstance; onDataChanged?: () => void }, ref) => {
    const [searchText, setSearchText] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchedColumn, setSearchedColumn] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [tableData, setTableData] = useState<SparepartDataType[]>([]);
    React.useEffect(() => {
        if (props.onDataChanged) props.onDataChanged();
    }, [tableData, props.onDataChanged]);
    const handleOk = () => setIsModalOpen(false);

    const handleSelectWorkOrder = async (record: any) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/work-order-detail/${record.invoiceWorkOrderNo}`);
            const data = res.data;
            props.form.setFieldsValue({
                invoice_work_order_no: data.invoice_no,
                work_order_no: record.workOrderNo,
                warehouse: data.warehouse,
                customer: data.customer_name,
                no_polisi: data.no_polisi,
                chassis_no: data.chassis_no,
                service_advisor: data.service_advisor,
                bill_to: data.bill_to,
            });
            setIsModalOpen(false);
        } catch (err) { console.error(err); }
    };

    const handleAddItems = async (selectedKeys: React.Key[]) => {
        const invoiceNo = props.form.getFieldValue("invoice_work_order_no");
        if (!invoiceNo || selectedKeys.length === 0) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/add-items`, {
                invoice_no: invoiceNo,
                selected_ids: selectedKeys,
            });

            const newItems = res.data
                .filter((item: any) => item.type === "1")
                .map((item: any) => ({
                    key: `${item.id}-${Date.now()}`,
                    type: item.type,
                    jobType: item.job_type,
                    itemNo: item.item_no,
                    description: item.description,
                    qty: item.qty,
                    qtyReturn: item.qty,
                    uom: item.uom,
                    price: item.price,
                    discPercent: item.disc_percent,
                    discAmount: item.disc_amount,
                    total: (Number(item.price) * Number(item.qty)) - (Number(item.disc_amount) * Number(item.qty)),
                }));

            setTableData(currentData => [...currentData, ...newItems]);
        } catch (err) {
            console.error("Failed to add items", err);
        }
    };

    const showModal = () => {
        setIsModalOpen(true);
    }

    const deleteSelectedItems = () => {
        setTableData(prev => prev.filter(item => !selectedRowKeys.includes(item.key)));
        setSelectedRowKeys([]);
    };

    useImperativeHandle(ref, () => ({
        showModal,
        addItems: handleAddItems,
        deleteSelectedItems,
        clearData: () => setTableData([]),
        setData: (data: SparepartDataType[]) => setTableData(data),
        getData: () => tableData,
    }));

    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: SparepartDataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: SparepartDataIndex): TableColumnType<SparepartDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        render: (text) => text,
    });

    const [columns, setColumns] = useState<TableColumnsType<SparepartDataType>>([
        { title: 'Type', dataIndex: 'type', key: 'type', width: 120, ellipsis: true, sorter: (a, b) => a.type.length - b.type.length, ...getColumnSearchProps('type') },
        { title: 'Job Type', dataIndex: 'jobType', key: 'jobType', width: 120, ellipsis: true, sorter: (a, b) => a.jobType.length - b.jobType.length, ...getColumnSearchProps('jobType') },
        { title: 'Item No', dataIndex: 'itemNo', key: 'itemNo', width: 150, ellipsis: true, sorter: (a, b) => a.itemNo.length - b.itemNo.length, ...getColumnSearchProps('itemNo') },
        { title: 'Description', dataIndex: 'description', key: 'description', width: 250, ellipsis: true, sorter: (a, b) => a.description.length - b.description.length, ...getColumnSearchProps('description') },
        { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, ellipsis: true, sorter: (a, b) => a.qty - b.qty, ...getColumnSearchProps('qty') },
        {
            title: 'Qty Return', dataIndex: 'qtyReturn', key: 'qtyReturn', width: 130, ellipsis: true, sorter: (a, b) => a.qtyReturn - b.qtyReturn, ...getColumnSearchProps('qtyReturn'), render: (text, record) => (
                <>
                    <Input type="number" value={text} onChange={(e) => { const newQty = parseFloat(e.target.value) || 0; setTableData(prev => prev.map(item => { if (item.key === record.key) { const price = Number(item.price) || 0; const disc = Number(item.discAmount) || 0; return { ...item, qtyReturn: newQty, total: (price + disc) * newQty }; } return item; })); }} />
                    {record.qtyReturn > record.qty && (
                        <div style={{ color: 'red', fontSize: '11px', marginTop: '4px' }}>Oversupply return!</div>
                    )}
                </>
            )
        },
        { title: 'UoM', dataIndex: 'uom', key: 'uom', width: 80, ellipsis: true, sorter: (a, b) => a.uom.length - b.uom.length, ...getColumnSearchProps('uom') },
        { title: 'Price', dataIndex: 'price', key: 'price', width: 120, ellipsis: true, sorter: (a, b) => a.price - b.price, ...getColumnSearchProps('price'), render: (val: number) => val?.toLocaleString('id-ID') },
        { title: 'Disc%', dataIndex: 'discPercent', key: 'discPercent', width: 80, ellipsis: true, sorter: (a, b) => a.discPercent - b.discPercent, ...getColumnSearchProps('discPercent') },
        { title: 'Disc Amount', dataIndex: 'discAmount', key: 'discAmount', width: 120, ellipsis: true, sorter: (a, b) => a.discAmount - b.discAmount, ...getColumnSearchProps('discAmount'), render: (val: number) => val?.toLocaleString('id-ID') },
        { title: 'Total', dataIndex: 'total', key: 'total', width: 120, ellipsis: true, sorter: (a, b) => a.total - b.total, ...getColumnSearchProps('total'), render: (val: number) => val?.toLocaleString('id-ID') },
    ]);

    const handleResize = (index: number) => (e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = { ...newColumns[index], width: size.width };
            return newColumns;
        });
    };

    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onColumnResize: handleResize(index),
        }),
    }));

    const components = { header: { cell: ResizableTitle } };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
    };

    return (
        <>
            <Table
                rowSelection={{ type: 'checkbox', ...rowSelection }}
                columns={resizableColumns}
                dataSource={tableData}
                bordered
                scroll={{ x: 'max-content' }}
                components={components}
            />
            <Modal title="List Data Work Order" open={isModalOpen} footer={null} onCancel={() => setIsModalOpen(false)} width={1000}>
                <ListDataWorkOrder onSelect={handleSelectWorkOrder} />
            </Modal>
        </>
    );
});


const InvoiceReturnOperationTable = forwardRef((props: { form: FormInstance; onDataChanged?: () => void }, ref) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [tableData, setTableData] = useState<OperationDataType[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    React.useEffect(() => {
        if (props.onDataChanged) props.onDataChanged();
    }, [tableData, props.onDataChanged]);

    useImperativeHandle(ref, () => ({
        addItems: handleAddItems,
        deleteSelectedItems: () => {
            setTableData(prev => prev.filter(item => !selectedRowKeys.includes(item.key)));
            setSelectedRowKeys([]);
        },
        clearData: () => setTableData([]),
        setData: (data: OperationDataType[]) => setTableData(data),
        getExistingItems: () => tableData.map(item => item.operationNo),
        getData: () => tableData,
    }));


    const handleAddItems = async (selectedKeys: React.Key[]) => {
        const invoiceNo = props.form.getFieldValue("invoice_work_order_no");
        if (!invoiceNo || selectedKeys.length === 0) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/add-items`, {
                invoice_no: invoiceNo,
                selected_ids: selectedKeys,
            });


            const newItems = res.data
                .filter((item: any) => item.type === "4")
                .map((item: any) => ({
                    key: `${item.id}-${Date.now()}`,
                    type: item.type,
                    jobType: item.job_type,
                    operationNo: item.item_no,
                    description: item.description,
                    frt: item.qty,
                    price: item.price,
                    discPercent: item.disc_percent,
                    discAmount: item.disc_amount,
                    total: (Number(item.price) - Number(item.disc_amount)) * Number(item.qty),
                }));

            setTableData(currentData => [...currentData, ...newItems]);
        } catch (err) {
            console.error("Failed to add operation items", err);
        }
    };

    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: OperationDataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: OperationDataIndex): TableColumnType<OperationDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        render: (text) => text,
    });


    const [operationColumns, setOperationColumns] = useState<TableColumnsType<OperationDataType>>([
        { title: 'Type', dataIndex: 'type', key: 'type', width: 120, ellipsis: true, sorter: (a, b) => a.type.length - b.type.length, ...getColumnSearchProps('type') },
        { title: 'Job Type', dataIndex: 'jobType', key: 'jobType', width: 120, ellipsis: true, sorter: (a, b) => a.jobType.length - b.jobType.length, ...getColumnSearchProps('jobType') },
        { title: 'Operation No', dataIndex: 'operationNo', key: 'operationNo', width: 150, ellipsis: true, sorter: (a, b) => a.operationNo.length - b.operationNo.length, ...getColumnSearchProps('operationNo') },
        { title: 'Description', dataIndex: 'description', key: 'description', width: 250, ellipsis: true, sorter: (a, b) => a.description.length - b.description.length, ...getColumnSearchProps('description') },
        { title: 'FRT', dataIndex: 'frt', key: 'frt', width: 80, ellipsis: true, sorter: (a, b) => a.frt - b.frt, ...getColumnSearchProps('frt') },
        { title: 'Price', dataIndex: 'price', key: 'price', width: 120, ellipsis: true, sorter: (a, b) => a.price - b.price, ...getColumnSearchProps('price'), render: (val: number) => val?.toLocaleString('id-ID') },
        { title: 'Disc%', dataIndex: 'discPercent', key: 'discPercent', width: 80, ellipsis: true, sorter: (a, b) => a.discPercent - b.discPercent, ...getColumnSearchProps('discPercent') },
        { title: 'Disc Amount', dataIndex: 'discAmount', key: 'discAmount', width: 120, ellipsis: true, sorter: (a, b) => a.discAmount - b.discAmount, ...getColumnSearchProps('discAmount'), render: (val: number) => val?.toLocaleString('id-ID') },
        { title: 'Total', dataIndex: 'total', key: 'total', width: 120, ellipsis: true, sorter: (a, b) => a.total - b.total, ...getColumnSearchProps('total'), render: (val: number) => val?.toLocaleString('id-ID') },
    ]);


    const handleOperationResize = (index: number) => (e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setOperationColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = { ...newColumns[index], width: size.width };
            return newColumns;
        });
    };


    const resizableOperationColumns = operationColumns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onColumnResize: handleOperationResize(index),
        }),
    }));

    const components = { header: { cell: ResizableTitle } };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
    };

    return (
        <Table
            rowSelection={{ type: 'checkbox', ...rowSelection }}
            columns={resizableOperationColumns}
            dataSource={tableData}
            bordered
            scroll={{ x: 'max-content' }}
            components={components}
        />
    );
});

const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const debounced = (...args: Parameters<F>) => {
        if (timeout !== null) {
            clearTimeout(timeout);
            timeout = null;
        }
        timeout = setTimeout(() => func(...args), waitFor);
    };

    return debounced;
};

export function useInvoiceReturnAutosave(
    form: FormInstance,
    getSparepartData: () => SparepartDataType[],
    getOperationData: () => OperationDataType[],
    isLocked: boolean
) {
    const [autosaveStatus, setAutosaveStatus] = useState<'stopped' | 'processing' | 'error' | 'idle'>('stopped');
    const woNo = Form.useWatch('invoice_work_order_no', form);

    const recalculateTotals = useCallback(async () => {
        const spareparts = getSparepartData();
        const operations = getOperationData();
        const itemTotal = [...spareparts, ...operations].reduce((sum, item) => sum + (Number(item.total) || 0), 0);
        const itemDiscount = [...spareparts, ...operations].reduce((sum, item) => sum + (Number(item.discAmount) || 0), 0);
        const total = itemTotal;
        try {
            const ppnRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ppn`);
            const vppn = ppnRes.data?.value_ppn || 0.11;
            const vat = total * vppn;
            const grandTotal = total + vat;
            form.setFieldsValue({
                item_total: itemTotal,
                item_discount: itemDiscount,
                total: total,
                vat: vat,
                grand_total: grandTotal
            });
        } catch (err) {
            console.error("Failed to fetch PPN", err);
        }
    }, [getSparepartData, getOperationData, form]);

    useEffect(() => {
        if (woNo && !isLocked && autosaveStatus === 'stopped') {
            setAutosaveStatus('idle');
        } else if (!woNo && autosaveStatus !== 'stopped') {
            setAutosaveStatus('stopped');
        }
    }, [woNo, isLocked, autosaveStatus]);

    useEffect(() => {
        recalculateTotals();
    }, [recalculateTotals]);

    useEffect(() => {
        const spareparts = getSparepartData();
        const operations = getOperationData();
        const itemTotal = [...spareparts, ...operations].reduce((sum, item) => sum + (Number(item.total) || 0), 0);
        const itemDiscount = [...spareparts, ...operations].reduce((sum, item) => sum + (Number(item.discAmount) || 0), 0);
        const total = itemTotal;

        const calculateAll = async () => {
            try {
                const ppnRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ppn`);
                const vppn = ppnRes.data?.value_ppn || 0.11;
                const vat = total * vppn;
                const grandTotal = total + vat;

                form.setFieldsValue({
                    item_total: Math.round(itemTotal),
                    item_discount: Math.round(itemDiscount),
                    total: Math.round(total),
                    vat: Math.round(vat),
                    grand_total: Math.round(grandTotal)
                });
            } catch (err) {
                console.error("Failed to fetch PPN", err);
            }
        };
        calculateAll();
    }, [getSparepartData(), getOperationData(), form]);
    const saveToServer = useCallback(async () => {
        const invoiceWorkOrderNo = form.getFieldValue('invoice_work_order_no');
        const currentStatus = form.getFieldValue('status');
        const isDraft = currentStatus === '1' || currentStatus === 1 || currentStatus === 'Draft' || currentStatus === 'Draf';

        if (!invoiceWorkOrderNo || isLocked || !isDraft) {
            setAutosaveStatus('stopped');
            return;
        }

        setAutosaveStatus('processing');

        try {
            await recalculateTotals();
            const formData = form.getFieldsValue(true);
            const sparepartItems = getSparepartData();
            const operationItems = getOperationData();

            const mappedSparepartItems = sparepartItems.map(item => ({
                invoice_return_no: formData.invoice_return_no,
                type: item.type,
                job_type: item.jobType,
                item_no: item.itemNo,
                description: item.description,
                qty_return: item.qtyReturn,
                uom: item.uom,
                price: item.price,
                disc_percent: item.discPercent,
                disc_amount: item.discAmount,
                total: item.total,
                frt: null,
            }));

            const mappedOperationItems = operationItems.map(item => ({
                invoice_return_no: formData.invoice_return_no,
                type: item.type,
                job_type: item.jobType,
                item_no: item.operationNo,
                description: item.description,
                frt: item.frt,
                price: item.price,
                disc_percent: item.discPercent,
                disc_amount: item.discAmount,
                total: item.total,
                qty_return: null,
                uom: null,
            }));

            const payload = {
                formData: { ...formData, status: formData.status === 'Draft' ? '1' : formData.status },
                tableItems: [...mappedSparepartItems, ...mappedOperationItems],
            };

            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/autosave`, payload);

            setAutosaveStatus('idle');
        } catch (error) {
            console.error("Autosave failed:", error);
            setAutosaveStatus('error');
        }
    }, [form, getSparepartData, getOperationData, isLocked]);
    const debouncedSaveRef = useRef(debounce(saveToServer, 1500));
    const triggerSave = useCallback(() => {
        recalculateTotals();
        debouncedSaveRef.current();
    }, [recalculateTotals]);
    return { autosaveStatus: isLocked ? 'stopped' : autosaveStatus, triggerSave };
}

export function useInvoiceReturnFormLogic(
    form: FormInstance,
    tableRef: React.RefObject<any>,
    operationTableRef: React.RefObject<any>,
    triggerSave: () => void,
    setIsLocked: React.Dispatch<React.SetStateAction<boolean>>
) {
    const { userData } = useAuth();
    const loadData = useCallback(async (returnNo: string) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/detail/${returnNo}`);
            const { formData, items } = res.data;
            const roundedFormData = {
                ...formData,
                item_total: formData.item_total != null ? Math.round(Number(formData.item_total)) : formData.item_total,
                item_discount: formData.item_discount != null ? Math.round(Number(formData.item_discount)) : formData.item_discount,
                total: formData.total != null ? Math.round(Number(formData.total)) : formData.total,
                vat: formData.vat != null ? Math.round(Number(formData.vat)) : formData.vat,
                grand_total: formData.grand_total != null ? Math.round(Number(formData.grand_total)) : formData.grand_total,
            };
            form.setFieldsValue(formData);
            const spareparts = items.filter((i: any) => i.type === "1").map((i: any) => ({ ...i, key: i.id, jobType: i.job_type, itemNo: i.item_no, qty: i.qty, qtyReturn: i.qty_return, discPercent: i.disc_percent, discAmount: i.disc_amount }));
            const operations = items.filter((i: any) => i.type === "4").map((i: any) => ({ ...i, key: i.id, jobType: i.job_type, operationNo: i.item_no, discPercent: i.disc_percent, discAmount: i.disc_amount }));
            tableRef.current?.setData(spareparts);
            operationTableRef.current?.setData(operations);
            setIsLocked(formData.status !== '1' && formData.status !== 1);
        } catch (err) { console.error("Load data failed", err); }
    }, [form, tableRef, operationTableRef, setIsLocked]);
    const fetchInitialData = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/initial-data`);
            form.setFieldsValue({ date_return: res.data.date_return, status: '1' });
        } catch (err) { console.error("Fetch initial data failed", err); }
    }, [form]);
    const handleNew = () => {
        form.resetFields();
        tableRef.current?.clearData();
        operationTableRef.current?.clearData();
        setIsLocked(false);
        fetchInitialData();
    };
    const handleSubmit = async () => {
        const returnNo = form.getFieldValue('invoice_return_no');
        const woNo = form.getFieldValue('invoice_work_order_no');
        if (!returnNo) return;
        try {
            const checkRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/check-payment/${woNo}`);
            if (checkRes.data && Number(checkRes.data.saldo_payment) > 0) {
                Modal.error({
                    title: 'Informasi Peringatan',
                    content: 'Invoice dalam Transaksi, tidak bisa di proses untuk return. Hubungi Admininstrator',
                });
                return;
            }
        } catch (err) {
            console.error("Payment check failed", err);
        }
        Modal.confirm({
            title: 'Peringatan',
            icon: <WarningOutlined style={{ color: '#faad14' }} />,
            content: 'apakah anda yakin melakukan return?',
            okText: 'OK',
            cancelText: 'Batal',
            onOk: () => {
                let remarkValue = `Pembatalan Invoice "${returnNo}"`;
                const RemarkInputComponent = () => {
                    const prefix = `Pembatalan Invoice "${returnNo}"`;
                    const [val, setVal] = useState(prefix);
                    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const input = e.target.value;
                        if (input.startsWith(prefix)) {
                            setVal(input);
                            remarkValue = input;
                        } else {
                            setVal(prefix);
                            remarkValue = prefix;
                        }
                    };
                    return (
                        <div style={{ marginTop: '10px' }}>
                            <Input.TextArea
                                rows={3}
                                value={val}
                                onChange={handleChange}
                            />
                        </div>
                    );
                };
                Modal.confirm({
                    title: 'Input Remark Pembatalan',
                    content: <RemarkInputComponent />,
                    okText: 'Submit',
                    cancelText: 'Batal',
                    onOk: async () => {
                        try {
                            const warehouse = form.getFieldValue('warehouse');
                            const grand_total = form.getFieldValue('grand_total');
                            const kode_grup1 = userData?.kode_grup1 || '';
                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/submit/${returnNo}`, {
                                warehouse,
                                grand_total,
                                kode_grup1,
                                remark: remarkValue
                            });
                            form.setFieldsValue({ status: '3' });
                            setIsLocked(true);
                            notification.success({ message: 'Berhasil', description: 'proses berhasil, menunggu Approval !' });
                        } catch (err) {
                            notification.error({ message: 'Gagal', description: 'Gagal melakukan submit data' });
                        }
                    }
                });
            }
        });
    };
    const handleVoid = async () => {
        const invoiceNo = form.getFieldValue('invoice_return_no');
        if (!invoiceNo) return;
        Modal.confirm({
            title: 'Void Data',
            content: 'Apakah anda yakin ingin menghapus data ini?',
            onOk: async () => {
                try {
                    await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/void/${invoiceNo}`);
                    setIsLocked(true);
                    handleNew();
                    notification.success({ message: 'Data berhasil di-void' });
                } catch (err) {
                    notification.error({ message: 'Gagal melakukan Void data' });
                }
            },
        });
    };
    const handleAddItemsFromModal = (selectedKeys: React.Key[], allModalItems: any[]) => {
        const sparepartSelectedKeys: React.Key[] = [];
        const operationSelectedKeys: React.Key[] = [];
        allModalItems.forEach(item => {
            if (selectedKeys.includes(item.key)) {
                if (item.type === "1") sparepartSelectedKeys.push(item.key);
                else if (item.type === "4") operationSelectedKeys.push(item.key);
            }
        });
        if (sparepartSelectedKeys.length > 0) tableRef.current?.addItems(sparepartSelectedKeys);
        if (operationSelectedKeys.length > 0) operationTableRef.current?.addItems(operationSelectedKeys);
    };
    return { fetchInitialData, handleNew, handleSubmit, handleVoid, handleAddItemsFromModal, loadData };
}

export { InvoiceReturnTable, InvoiceReturnOperationTable }; 